import { Component, OnInit } from '@angular/core';
import { FeatureFlagService } from 'src/app/modules/feature-flags/services/feature-flag/feature-flag.service';
import { ClickstreamEventTypeName } from 'src/app/modules/olp-api/enums/clickstream-events';
import { ClickstreamService } from 'src/app/modules/olp-api/services/clickstream/clickstream.service';
import { QuestionLogService } from 'src/app/modules/olp-api/services/question-log/question-log.service';
import { ClickstreamActivityData } from 'src/app/modules/olp-api/types';
import { ToggleComponent } from 'src/app/shared/components/toggle/toggle.component';
import { FeatureFlags } from 'src/app/shared/enums/feature-flags';
import { MultipleChoiceTypes } from 'src/app/shared/enums';
import { ActivityService, InactivityService } from 'src/app/shared/services';
import { ConstructClickstreamService } from 'src/app/shared/services/construct-clickstream/construct-clickstream.service';
import { CookieService, CookieOptions } from 'ngx-cookie-service';
import { WA_WINDOW } from '@ng-web-apis/common';
import { inject } from '@angular/core';
import { QuestionState } from 'src/app/modules/olp-api/enums/question-log';
import { SkipQuestionLogSortTypes, TemplateID } from 'src/app/shared/enums';
import { Population } from 'src/app/shared/interfaces';
import { FormsModule } from '@angular/forms';
import {
  constructedResponseQuestions,
  extendedConstructedResponseQuestions,
  multiChoiceQuestions,
} from 'src/app/data';
import { TEST_CHOICE_TYPES } from 'src/app/data/choice-types';
import { fillInTheBlankTileText } from 'src/app/data/fill-in-the-blank-tile-text-questions';
import { inlineChoiceQuestions } from 'src/app/data/inline-choice-questions';
import { multiChoiceImageQuestions } from 'src/app/data/multi-choice-image-questions';
import { multiSelectImageQuestions } from 'src/app/data/multi-select-image-questions';
import { multiSelectQuestions } from 'src/app/data/multi-select-questions';
import { sortingBuckets } from 'src/app/data/sorting-buckets';
import { sortingBucketsImages } from 'src/app/data/sorting-buckets-images';
import { textEntryQuestions } from 'src/app/data/text-entry-questions';
import { ConfigurationService } from 'src/app/modules/configuration/services/configuration/configuration.service';
import { getMainDomain } from 'src/app/shared/utils/util';

@Component({
  selector: 'htc-clickstream-debug-menu',
  standalone: true,
  imports: [ToggleComponent, FormsModule],
  templateUrl: './clickstream-debug-menu.component.html',
  styleUrl: './clickstream-debug-menu.component.scss',
})
export class ClickstreamDebugMenuComponent implements OnInit {
  readonly SESSION_ID_STORAGE = 'LastSessionId';
  readonly SESSION_COOKIE_KEY = 'clickstream.session';
  readonly MultipleChoiceTypes = MultipleChoiceTypes;
  templateNames = Object.values(TemplateID).slice(
    0,
    Math.floor(Object.values(TemplateID).length / 2) - 1
  );
  templateIDs = Object.values(TemplateID).slice(
    Math.floor(Object.values(TemplateID).length / 2) + 1,
    Object.values(TemplateID).length
  );
  currentTeiID = 0;
  private cookieExpiry = 2 * 3.6e6;
  private window = inject(WA_WINDOW);

  useAuth = false;
  useClickstream = false;
  useCookie = true;

  json = JSON;

  episodeTitle = 'Episode Title';
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  defaultQuestionsByType: any[] = [
    { ...multiChoiceQuestions[TEST_CHOICE_TYPES.TEST_R_MC_SHORT] },
    { ...inlineChoiceQuestions[TEST_CHOICE_TYPES.TEST_R_IC_SHORT] },
    { ...multiChoiceQuestions[TEST_CHOICE_TYPES.TEST_R_MC_TRUE_FALSE] },
    { ...textEntryQuestions[TEST_CHOICE_TYPES.TEST_R_TE_SHORT] },
    { ...multiChoiceImageQuestions[TEST_CHOICE_TYPES.TEST_R_MCI_SHORT] },
    { ...multiSelectQuestions[TEST_CHOICE_TYPES.TEST_R_MS_SHORT] },
    { ...multiSelectImageQuestions[TEST_CHOICE_TYPES.TEST_R_MSI_SHORT] },
    { ...multiSelectImageQuestions[TEST_CHOICE_TYPES.TEST_R_MSI_SHORT] },
    {},
    {},
    {},
    { ...fillInTheBlankTileText[TEST_CHOICE_TYPES.TEST_R_DND_BLANK_SHORT] },
    { ...constructedResponseQuestions[TEST_CHOICE_TYPES.TEST_R_SCR_SHORT] },
    { ...constructedResponseQuestions[TEST_CHOICE_TYPES.TEST_R_SCRR_SHORT] },
    {
      ...extendedConstructedResponseQuestions[
        TEST_CHOICE_TYPES.TEST_R_ECR_SHORT
      ],
    },
    {
      ...extendedConstructedResponseQuestions[
        TEST_CHOICE_TYPES.TEST_R_ECRR_SHORT
      ],
    },
    { ...sortingBuckets[TEST_CHOICE_TYPES.TEST_R_DND_SORT_BUCKETS_SHORT] },
    { ...sortingBucketsImages['test1'] },
  ];

  questionId = '0';
  question = 'default question prompt';
  correctAnswer = 'correct';
  discrims: string[] = ['correct', 'incorrect', 'Incorrect', 'wrong'];

  responses: string[] = [];
  maxAttempts = 2;
  currAttempt = 0;
  timeAsked = 0;

  flags = FeatureFlags;

  constructor(
    public clickstreamService: ClickstreamService,
    private questionLogService: QuestionLogService,
    public featureFlagService: FeatureFlagService,
    private inactivityService: InactivityService,
    private constructClickstreamService: ConstructClickstreamService,
    private activityService: ActivityService,
    private cookieService: CookieService,
    private configurationService: ConfigurationService
  ) {}

  ngOnInit(): void {
    this.setFlagsFromService();
  }

  updateFlag(flagToUpdate: string, state: boolean): void {
    this.featureFlagService.setFlag(flagToUpdate, state);
    this.featureFlagService.saveCurrentAsOverride();
    this.setFlagsFromService();
  }

  async startSession(fresh: boolean): Promise<void> {
    const sessionId = fresh
      ? crypto.randomUUID()
      : this.useCookie
        ? this.getSessionIdFromCookie()
        : this.getSessionIdFromLocalStorage();

    if (this.useCookie) {
      this.saveSessionIdToCookie(sessionId);
    } else {
      this.saveSessionIdToLocalStorage(sessionId);
    }

    this.clickstreamService.setSessionId(sessionId);

    await this.clickstreamService.checkForAccessToken();

    const startSessionName = 'Microlearning Home';
    const activityData: ClickstreamActivityData = {
      activityUuid: '0',
      contentOid: 0,
      activityName: startSessionName,
      category: 'n/a',
      scoreCriteria: 'n/a',
      difficulty: 0,
    };
    this.clickstreamService.clickstream.initializeClickstreamCache(
      activityData,
      false
    );
    this.clickstreamService.startSession(startSessionName);
  }

  startActivity(): void {
    this.clickstreamService.startActivity(
      'uuid',
      0,
      'activity',
      'category',
      'scoreCriteria',
      0
    );
    this.inactivityService.startInactivityTracking();
  }

  continueActivity(): void {
    this.clickstreamService.continueAmiraActivity('inboxActivityId');
  }

  completeActivity(): void {
    this.inactivityService.pauseInactivityTracking();
    this.clickstreamService.completeActivity(50, 1, 0);
  }

  interruptActivity(): void {
    this.inactivityService.pauseInactivityTracking();
    this.clickstreamService.addEventFromProperties(
      ClickstreamEventTypeName.ActivityInterrupt,
      'Exit',
      'Exit Button'
    );
  }

  reportInactivity(): void {
    const inactivity = this.inactivityService.getCurrentInactiveTime();
    this.clickstreamService.addEventFromProperties(
      ClickstreamEventTypeName.Inactivity,
      'Inactivity',
      inactivity / 1000
    );
  }

  askQuestion(): void {
    this.questionLogService.askQuestion(
      this.questionId,
      this.question,
      this.correctAnswer,
      this.discrims,
      false,
      3,
      !SkipQuestionLogSortTypes.includes(3),
      QuestionState.initial,
      0
    );
    this.timeAsked = Date.now();
    this.currAttempt = 0;
    this.responses = [];
  }

  randomizeQuestion(): Promise<void> {
    return fetch('https://opentdb.com/api.php?amount=1').then(res => {
      if (res.ok && res.status === 200) {
        res.json().then(questionData => {
          this.question = questionData.results[0].question;
          this.correctAnswer = questionData.results[0].correct_answer;
          this.discrims = [
            this.correctAnswer,
            ...questionData.results[0].incorrect_answers,
          ];
        });
      }
    });
  }

  answerCorrect(): void {
    const sortResponse = !SkipQuestionLogSortTypes.includes(3);
    this.questionLogService.answerQuestion(
      {} as Population,
      [this.correctAnswer],
      sortResponse,
      QuestionState.askQuestion,
      0
    );
  }

  answerIncorrect(): void {
    const answer = this.getRandomIncorrectAnswer();
    const sortResponse = !SkipQuestionLogSortTypes.includes(3);
    this.questionLogService.answerQuestion(
      {} as Population,
      [answer],
      sortResponse,
      QuestionState.askQuestion,
      0
    );
    this.currAttempt++;
    this.responses.push(answer);
  }

  sendClickstream(): void {
    this.clickstreamService.saveClickstreamData();
  }

  clearEvents(): void {
    this.clickstreamService.clearEvents();
  }

  generateClickstream(): void {
    this.constructClickstreamService.generateClickstream(
      this.activityService.exportIncompleteActivityState(),
      this.activityService.currActivity,
      0
    );
  }

  private getRandomIncorrectAnswer(): string {
    const discrimsMinusCorrect = this.discrims.filter(
      d => d !== this.correctAnswer
    );
    return discrimsMinusCorrect[
      Math.floor(Math.random() * discrimsMinusCorrect.length)
    ];
  }

  private setFlagsFromService(): void {
    this.useAuth = this.featureFlagService.isFlagEnabled(
      FeatureFlags.AUTHENTICATE
    );
    this.useClickstream = this.featureFlagService.isFlagEnabled(
      FeatureFlags.USE_CLICKSTREAM
    );
  }

  private saveSessionIdToLocalStorage(sessionId: string): void {
    localStorage.setItem(this.SESSION_ID_STORAGE, sessionId);
  }

  private saveSessionIdToCookie(sessionId: string): void {
    const expires = new Date(Date.now() + this.cookieExpiry);
    const baseUrlDomain = new URL(this.configurationService.config.baseUrl)
      .hostname;
    const domain = !this.window.location.origin.includes('localhost')
      ? `.${getMainDomain(baseUrlDomain)}`
      : 'localhost';
    const cookieOptions: CookieOptions = {
      expires,
      domain,
      path: '/',
      secure: true,
      sameSite: 'Strict',
    };
    this.cookieService.set(this.SESSION_COOKIE_KEY, sessionId, cookieOptions);
  }

  private getSessionIdFromLocalStorage(): string {
    const storageId = localStorage.getItem(this.SESSION_ID_STORAGE);
    if (!storageId) {
      console.warn(
        'There was no session id found in local storage, returning new uuid.'
      );
    }
    return storageId ?? crypto.randomUUID();
  }

  private getSessionIdFromCookie(): string {
    const cookieId = this.cookieService.get(this.SESSION_COOKIE_KEY);
    if (!cookieId) {
      console.warn(
        'There was no session id found in cookie, returning new uuid.'
      );
    }

    return cookieId ?? crypto.randomUUID();
  }
}
